import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import api from "../lib/api";
import { Button } from "./ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (s) => {
  if (!s || typeof s !== "string" || !s.includes("-")) return s || "";
  return s.split("-").map((p) => {
    const t = String(p).trim().padStart(4, "0");
    return /^\d{4}$/.test(t) ? `${t.slice(0, 2)}:${t.slice(2)}` : p;
  }).join("–");
};

export const VisitCalendar = ({ clientId, token, preview }) => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (m) => {
    setBusy(true);
    try {
      const url = token
        ? `${BASE}/portal/${token}/calendar?month=${m}`
        : preview
          ? `/clients/${clientId}/portal-preview/calendar?month=${m}`
          : `/clients/${clientId}/calendar?month=${m}`;
      const r = token ? await axios.get(url) : await api.get(url);
      setData(r.data);
    } catch { setData({ days: {}, projected: {} }); }
    finally { setBusy(false); }
  }, [clientId, token, preview]);

  useEffect(() => { load(month); }, [month, load]);

  const shift = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(d.toISOString().slice(0, 7));
  };

  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const startPad = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = first.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="visit-calendar">
      <div className="flex items-center justify-between">
        <p className="font-heading text-sm font-semibold text-stone-900">{token ? "Completed visits" : "Visit schedule"}</p>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => shift(-1)} className="h-7 w-7 p-0" data-testid="cal-prev"><CaretLeft size={14} /></Button>
          <span className="min-w-32 text-center text-xs font-medium text-stone-600" data-testid="cal-month">{monthLabel}</span>
          <Button size="sm" variant="ghost" onClick={() => shift(1)} className="h-7 w-7 p-0" data-testid="cal-next"><CaretRight size={14} /></Button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wide text-stone-400">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`x${i}`} />;
          const iso = `${month}-${String(d).padStart(2, "0")}`;
          const actual = (data?.days || {})[iso] || [];
          const proj = (data?.projected || {})[iso] || [];
          return (
            <div key={iso} data-testid={`cal-day-${iso}`}
              className={`min-h-14 rounded-md border p-1 text-left ${actual.length ? "border-stone-200 bg-stone-50" : proj.length ? "border-dashed border-primary/40 bg-primary/5" : "border-transparent"}`}>
              <p className="text-[10px] font-semibold text-stone-500">{d}</p>
              {actual.map((v, j) => (
                <p key={j} className={`mt-0.5 truncate text-[10px] leading-tight ${v.missed ? "text-[#C0392B] line-through" : "text-stone-700"}`}>
                  {(v.caregiver || "").split(" ").slice(-1)[0]} {fmtTime(v.time)}
                  {v.source === "missed-punch" && <span className="text-stone-400"> · paper form{v.needs_signature ? " — signature needed" : ""}</span>}
                </p>
              ))}
              {proj.map((v, j) => (
                <p key={`p${j}`} className="mt-0.5 truncate text-[10px] italic leading-tight text-stone-400">
                  {(v.caregiver || "").split(" ").slice(-1)[0]} {fmtTime(v.time)}
                </p>
              ))}
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-stone-400" data-testid="cal-legend">
        {busy ? "Loading…" : token
          ? "Visits recorded electronically or by signed paper form"
          : "Solid = scheduled/actual visits from the visit log · dashed italic = projected from recent weekly patterns"}
      </p>
    </div>
  );
};
